<template>
  <div class="workspace-page organization-page">
    <section class="organization-intro">
      <div class="organization-intro__main">
        <span class="organization-intro__icon" aria-hidden="true">
          <i class="el-icon-office-building"></i>
        </span>
        <div>
          <span class="organization-intro__eyebrow">{{ $t("团队与部门") }}</span>
          <h2>{{ $t("清晰查看团队层级") }}</h2>
          <p>{{ $t("用清晰的层级查看部门关系和负责人，点击部门前的箭头即可展开或收起下级部门。") }}</p>
        </div>
      </div>
      <div v-if="user" class="organization-access-note">
        <i :class="isEnterpriseAdmin ? 'el-icon-s-custom' : 'el-icon-view'" aria-hidden="true"></i>
        <span>{{ isEnterpriseAdmin ? $t("管理员可通过节点菜单维护部门") : $t("当前为只读视图") }}</span>
      </div>
    </section>

    <section class="organization-panel">
      <div class="organization-toolbar">
        <div>
          <h3>{{ $t("部门层级") }}</h3>
          <p>{{ $t("负责人信息与管理操作集中展示在对应部门中。") }}</p>
        </div>
        <el-input
          v-model="filterText"
          class="organization-search"
          clearable
          prefix-icon="el-icon-search"
          :placeholder="$t('搜索部门或负责人')"
          @input="filterTree">
        </el-input>
      </div>

      <div v-if="!user" v-loading="true" class="organization-loading" :aria-label="$t('正在加载组织架构')"></div>
      <el-tree
        v-else-if="organizationStructureVisible"
        ref="tree"
        :filter-node-method="filterNode"
        :load="loadNode"
        :props="organizationStructure"
        default-expand-all
                :empty-text="$t('暂无部门数据')"
        lazy
        node-key="departmentId"
        class="organization-tree">
        <div
          slot-scope="{ node, data }"
          class="department-node"
          :class="{ 'department-node--root': isRootDepartment(data) }">
          <div class="department-node__main">
            <span class="department-node__copy">
              <span class="department-node__title-row">
                <strong>{{ data.name || $t("未命名部门") }}</strong>
              </span>
              <span v-if="!isRootDepartment(data)" class="department-node__leader">
                <i class="el-icon-user" aria-hidden="true"></i>
                {{ getLeaderName(data) || $t("未设置负责人") }}
              </span>
            </span>
          </div>

          <el-dropdown
            v-if="isEnterpriseAdmin"
            class="department-node__actions"
            placement="bottom-end"
            trigger="click"
            @click.native.stop
            @command="handleDepartmentCommand($event, node, data)">
            <button
              class="department-node__menu-button"
              type="button"
              :aria-label="$t('管理{p0}', { p0: data.name || $t('部门') })"
              @click.stop>
              <i class="el-icon-more" aria-hidden="true"></i>
            </button>
            <el-dropdown-menu slot="dropdown" class="department-action-menu">
              <el-dropdown-item command="add">
                <i class="el-icon-plus" aria-hidden="true"></i>
                <span>{{ $t("添加下级部门") }}</span>
              </el-dropdown-item>
              <el-dropdown-item v-if="!isRootDepartment(data)" command="edit">
                <i class="el-icon-edit-outline" aria-hidden="true"></i>
                <span>{{ $t("编辑部门") }}</span>
              </el-dropdown-item>
              <el-dropdown-item
                v-if="!isRootDepartment(data)"
                command="delete"
                divided
                class="department-action-menu__danger">
                <i class="el-icon-delete" aria-hidden="true"></i>
                <span>{{ $t("删除部门") }}</span>
              </el-dropdown-item>
            </el-dropdown-menu>
          </el-dropdown>
        </div>
      </el-tree>
    </section>

    <el-dialog
      :visible.sync="dialogVisible"
      class="department-dialog"
      :title="$t('提示')"
      width="420px">
      <p class="department-dialog__message">{{ dialogContent }}</p>
      <span slot="footer" class="dialog-footer">
        <el-button type="primary" @click="dialogVisible = false">{{ $t("确定") }}</el-button>
      </span>
    </el-dialog>

    <el-dialog
      :close-on-click-modal="false"
      :visible.sync="createDepartmentDialog.visible"
      class="department-dialog"
      :title="$t('添加下级部门')"
      width="480px">
      <p class="department-dialog__description">
        {{ $t("新部门将创建在“{p0}”下。", { p0: currentData.name || $t("当前部门") }) }}
      </p>
      <el-form label-position="top" @submit.native.prevent>
        <el-form-item :label="$t('部门名称')" required>
          <el-input
            v-model="createDepartmentDialog.departmentName"
            maxlength="30"
            :placeholder="$t('请输入部门名称')"
            show-word-limit
            @keyup.enter.native="createDepartmentButtonClick">
          </el-input>
        </el-form-item>
        <el-form-item :label="$t('部门负责人')" required>
          <el-select
            v-model="createDepartmentDialog.leader"
            :loading="employeeSearchLoading"
            :remote-method="onSearchEmployee"
            filterable
            :placeholder="$t('请选择负责人')"
            remote
            reserve-keyword>
            <el-option
              v-for="item in employees"
              :key="item.userId"
              :label="item.name || $t('当前负责人')"
              :value="item.userId">
            </el-option>
          </el-select>
        </el-form-item>
      </el-form>
      <span slot="footer" class="dialog-footer">
        <el-button @click="createDepartmentDialog.visible = false">{{ $t("取消") }}</el-button>
        <el-button
          :loading="createDepartmentDialog.loading"
          type="primary"
          @click="createDepartmentButtonClick">{{ $t("创建部门") }}</el-button>
      </span>
    </el-dialog>

    <el-dialog
      :close-on-click-modal="false"
      :visible.sync="editDepartmentDialog.visible"
      class="department-dialog"
      :title="$t('编辑部门')"
      width="480px">
      <p class="department-dialog__description">{{ $t("修改部门名称或重新指定负责人。") }}</p>
      <el-form label-position="top" @submit.native.prevent>
        <el-form-item :label="$t('部门名称')" required>
          <el-input
            v-model="editDepartmentDialog.departmentName"
            maxlength="30"
            :placeholder="$t('请输入部门名称')"
            show-word-limit
            @keyup.enter.native="editDepartmentButtonClick">
          </el-input>
        </el-form-item>
        <el-form-item :label="$t('部门负责人')" required>
          <el-select
            v-model="editDepartmentDialog.leader"
            :loading="employeeSearchLoading"
            :remote-method="onSearchEmployee"
            filterable
            :placeholder="$t('请选择负责人')"
            remote
            reserve-keyword>
            <el-option
              v-for="item in employees"
              :key="item.userId"
              :label="item.name || $t('当前负责人')"
              :value="item.userId">
            </el-option>
          </el-select>
        </el-form-item>
      </el-form>
      <span slot="footer" class="dialog-footer">
        <el-button @click="editDepartmentDialog.visible = false">{{ $t("取消") }}</el-button>
        <el-button
          :loading="editDepartmentDialog.loading"
          type="primary"
          @click="editDepartmentButtonClick">{{ $t("保存修改") }}</el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import { t } from "@/i18n";
import axios from "axios";
import app from "@/App";
import { getCompanyIdFromToken } from "@/utils/refreshToken";

export default {
  name: "OrganizationStructure",
  data() {
    return {
      organizationStructureVisible: true,
      organizationStructure: {
        label: 'name',
        children: 'zones',
        isLeaf: 'leaf'
      },
      user: null,
      filterText: '',
      dialogContent: '',
      dialogVisible: false,
      createDepartmentDialog: {
        visible: false,
        departmentName: '',
        leader: null,
        loading: false,
      },
      editDepartmentDialog: {
        visible: false,
        departmentName: '',
        leader: null,
        loading: false,
      },
      currentNode: {},
      currentData: {},
      employees: [],
      companyEmployees: [],
      employeeSearchLoading: false,
      employeeSearchRequestId: 0,
      rootDepartmentId: null,
    };
  },
  computed: {
    companyId() {
      return getCompanyIdFromToken() || (this.user && this.user.company && this.user.company.companyId) || null;
    },
    isEnterpriseAdmin() {
      return this.user && this.user.roleId === 1;
    }
  },
  created() {
    app.methods.getUser()
      .then(res => {
        this.user = res.data;
        this.loadCompanyEmployees();
      })
      .catch(error => {
        this.showRequestError(error, t("用户信息加载失败"));
      });
  },
  methods: {
    getLeaderName(data) {
      return data.leaderName || (data.leader && data.leader.name) || '';
    },
    isRootDepartment(data) {
      return Boolean(
        this.rootDepartmentId
        && data.departmentId != null
        && String(data.departmentId) === String(this.rootDepartmentId)
      );
    },
    showRequestError(error, fallbackMessage) {
      const responseMessage = error
        && error.response
        && error.response.data
        && error.response.data.message;
      this.$message.error(responseMessage || fallbackMessage);
    },
    loadCompanyEmployees() {
      if (!this.companyId) {
        this.companyEmployees = [];
        this.employees = [];
        return;
      }

      app.methods.getEmployees(this.companyId)
        .then((employees) => {
          this.companyEmployees = Array.isArray(employees) ? employees : [];
          this.employees = this.companyEmployees.slice();
        })
        .catch(error => {
          this.showRequestError(error, t("员工列表加载失败"));
        });
    },
    onSearchEmployee(keyword) {
      const requestId = ++this.employeeSearchRequestId;
      if (!keyword) {
        this.employeeSearchLoading = false;
        this.employees = this.companyEmployees.slice();
        return;
      }

      this.employeeSearchLoading = true;
      app.methods.searchEmployees(keyword)
        .then(res => {
          if (requestId !== this.employeeSearchRequestId) {
            return;
          }
          this.employees = res && res.data && Array.isArray(res.data.employees)
            ? res.data.employees
            : [];
        })
        .catch(error => {
          if (requestId === this.employeeSearchRequestId) {
            this.showRequestError(error, t("负责人搜索失败"));
          }
        })
        .finally(() => {
          if (requestId === this.employeeSearchRequestId) {
            this.employeeSearchLoading = false;
          }
        });
    },
    filterTree(value) {
      if (this.$refs.tree) {
        this.$refs.tree.filter(value);
      }
    },
    filterNode(value, data) {
      if (!value) {
        return true;
      }
      const keyword = value.trim().toLowerCase();
      return `${data.name || ''} ${this.getLeaderName(data)}`.toLowerCase().includes(keyword);
    },
    handleDepartmentCommand(command, node, data) {
      if (command === 'add') {
        this.addDepartment(node, data);
        return;
      }
      if (command === 'edit') {
        this.openEditDepartmentDialog(node, data);
        return;
      }
      if (command === 'delete') {
        this.deleteDepartment(node, data);
      }
    },
    addDepartment(node, data) {
      this.currentNode = node;
      this.currentData = data;
      this.openCreateDepartmentDialog();
    },
    async deleteDepartment(node, data) {
      try {
        await this.$confirm(
          t("删除“{p0}”后将无法恢复，请确认该部门下已无下级部门或员工。", { p0: data.name }),
          t("删除部门"),
          {
            confirmButtonText: t("删除"),
            cancelButtonText: t("取消"),
            confirmButtonClass: 'el-button--danger',
            type: 'warning'
          }
        );
        const res = await axios.delete(`${app.url}/departments/${data.departmentId}`);
        if (res.data && res.data.error) {
          this.$message.error(res.data.message);
          return;
        }
        this.$refs.tree.remove(data);
        this.$message.success(t("部门已删除"));
      } catch (error) {
        if (error !== 'cancel' && error !== 'close') {
          this.showRequestError(error, t("删除部门失败"));
        }
      }
    },
    showAlertDialog(content) {
      this.dialogContent = content;
      this.dialogVisible = true;
    },
    openCreateDepartmentDialog() {
      this.createDepartmentDialog.departmentName = '';
      this.createDepartmentDialog.leader = this.user ? this.user.userId : null;
      this.employees = this.companyEmployees.slice();
      this.ensureEmployeeOption(
        this.createDepartmentDialog.leader,
        this.user && this.user.name
      );
      this.createDepartmentDialog.visible = true;
    },
    async createDepartmentButtonClick() {
      if (this.createDepartmentDialog.loading) {
        return;
      }
      const departmentName = this.createDepartmentDialog.departmentName.trim();
      if (!departmentName) {
        this.$message.warning(t("请输入部门名称"));
        return;
      }
      if (!this.createDepartmentDialog.leader) {
        this.$message.warning(t("请选择部门负责人"));
        return;
      }
      if (!this.companyId) {
        this.$message.error(t("无法获取企业信息"));
        return;
      }

      this.createDepartmentDialog.loading = true;
      try {
        const res = await axios.post(
          `${app.url}/companies/${this.companyId}/departments`,
          {
            name: departmentName,
            parentId: this.currentData.departmentId,
            leaderId: this.createDepartmentDialog.leader,
          }
        );
        if (res.data && res.data.error) {
          this.$message.error(res.data.message);
          return;
        }
        this.createDepartmentDialog.visible = false;
        this.refreshTree();
        this.$message.success(t("部门创建成功"));
      } catch (error) {
        this.showRequestError(error, t("创建部门失败"));
      } finally {
        this.createDepartmentDialog.loading = false;
      }
    },
    async editDepartmentButtonClick() {
      if (this.editDepartmentDialog.loading) {
        return;
      }
      const departmentName = this.editDepartmentDialog.departmentName.trim();
      if (!departmentName) {
        this.$message.warning(t("请输入部门名称"));
        return;
      }
      if (!this.editDepartmentDialog.leader) {
        this.$message.warning(t("请选择部门负责人"));
        return;
      }

      this.editDepartmentDialog.loading = true;
      try {
        const res = await axios.put(
          `${app.url}/departments/${this.currentData.departmentId}`,
          {
            name: departmentName,
            leaderId: this.editDepartmentDialog.leader,
          }
        );
        if (res.data && res.data.error) {
          this.$message.error(res.data.message);
        } else if (res.data.status === 3) {
          this.showAlertDialog(res.data.info);
        } else {
          this.editDepartmentDialog.visible = false;
          this.refreshTree();
          this.$message.success(t("部门信息已更新"));
        }
      } catch (error) {
        this.showRequestError(error, t("更新部门失败"));
      } finally {
        this.editDepartmentDialog.loading = false;
      }
    },
    ensureEmployeeOption(leaderId, leaderName) {
      if (!leaderId || this.employees.some(employee => employee.userId === leaderId)) {
        return;
      }
      this.employees = this.employees.concat({
        userId: leaderId,
        name: leaderName || ''
      });
    },
    refreshTree() {
      this.organizationStructureVisible = false;
      this.$nextTick(() => {
        this.organizationStructureVisible = true;
        this.$nextTick(() => this.filterTree(this.filterText));
      });
    },
    async getDepartments(departmentId, includeRoot = false) {
      if (!this.companyId) {
        return [];
      }
      const shouldIncludeRoot = includeRoot || !this.rootDepartmentId;
      const response = await app.methods.getAllDepartmentsOfCompany(
        this.companyId,
        departmentId,
        shouldIncludeRoot
      );
      const departments = Array.isArray(response.data) ? response.data : [];
      if (shouldIncludeRoot && !this.rootDepartmentId) {
        const rootIdFromUser = this.user && this.user.company ? this.user.company.rootDepartmentId : null;
        const rootFromData = departments.find(dep => dep.parentId == null);
        this.rootDepartmentId = rootIdFromUser || (rootFromData && rootFromData.departmentId) || null;
      }
      if (!includeRoot) {
        return departments;
      }
      if (this.rootDepartmentId) {
        const rootDepartment = departments.find(
          dep => String(dep.departmentId) === String(this.rootDepartmentId)
        );
        if (rootDepartment) {
          return [rootDepartment];
        }
      }
      return departments.filter(dep => dep.parentId == null);
    },
    loadNode(node, resolve) {
      const parentId = node.level === 0 ? undefined : node.data.departmentId;
      const includeRoot = node.level === 0;
      this.getDepartments(parentId, includeRoot)
        .then((departments) => {
          resolve(Array.isArray(departments) ? departments : []);
        })
        .catch(error => {
          resolve([]);
          this.showRequestError(error, t("部门数据加载失败"));
        });
    },
    openEditDepartmentDialog(node, data) {
      this.currentNode = node;
      this.currentData = data;
      this.editDepartmentDialog.departmentName = data.name;
      const leaderId = data.leaderId || (data.leader && data.leader.userId) || null;
      this.editDepartmentDialog.leader = leaderId;
      this.employees = this.companyEmployees.slice();
      this.ensureEmployeeOption(leaderId, this.getLeaderName(data));
      this.editDepartmentDialog.visible = true;
    },
  }
}
</script>

<style>
.organization-page {
  gap: 18px;
}

.organization-intro,
.organization-panel {
  border: 1px solid var(--border-light);
  border-radius: 16px;
  background: var(--surface-color);
  box-shadow: 0 4px 18px var(--shadow-color);
}

.organization-intro {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 24px;
  padding: 22px 24px;
}

.organization-intro__main {
  display: flex;
  min-width: 0;
  align-items: center;
  gap: 16px;
}

.organization-intro__icon {
  display: inline-flex;
  width: 52px;
  height: 52px;
  flex: 0 0 auto;
  align-items: center;
  justify-content: center;
  border-radius: 15px;
  background: var(--primary-soft);
  color: var(--primary-color);
  font-size: 24px;
}

.organization-intro__eyebrow {
  color: var(--primary-color);
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.12em;
}

.organization-intro h2,
.organization-toolbar h3 {
  margin: 0;
  color: var(--text-primary);
}

.organization-intro h2 {
  margin-top: 3px;
  font-size: 20px;
}

.organization-intro p,
.organization-toolbar p {
  margin: 5px 0 0;
  color: var(--text-muted);
  font-size: 13px;
  line-height: 1.6;
}

.organization-access-note {
  display: inline-flex;
  flex: 0 0 auto;
  align-items: center;
  gap: 8px;
  padding: 8px 11px;
  border-radius: 10px;
  background: var(--surface-hover);
  color: var(--text-regular);
  font-size: 12px;
}

.organization-panel {
  padding: 20px;
}

.organization-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 20px;
  padding: 0 2px 18px;
  border-bottom: 1px solid var(--border-light);
}

.organization-toolbar h3 {
  font-size: 16px;
}

.organization-search {
  width: 280px;
  flex: 0 0 auto;
}

.organization-loading {
  min-height: 180px;
}

.organization-tree {
  width: 100%;
  min-height: 150px;
  padding: 12px 0 2px;
  overflow: hidden;
  background: transparent;
  color: var(--text-primary);
}

.organization-page .el-tree-node__content {
  box-sizing: border-box;
  height: auto;
  min-height: 70px;
  padding: 4px 0;
  background: transparent;
}

.organization-page .el-tree-node__content:hover,
.organization-page .el-tree-node:focus > .el-tree-node__content {
  background: transparent !important;
}

.organization-page .el-tree-node__expand-icon {
  margin-right: 5px;
  color: var(--text-muted);
  font-size: 14px;
}

.organization-page .el-tree-node__expand-icon.is-leaf {
  color: transparent;
}

.department-node {
  display: flex;
  min-width: 0;
  flex: 1;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-right: 2px;
  padding: 10px 12px;
  overflow: hidden;
  border: 1px solid var(--border-light);
  border-radius: 12px;
  background: var(--surface-elevated);
  transition: border-color 0.2s ease, box-shadow 0.2s ease, transform 0.2s ease;
}

.organization-page .el-tree-node__content:hover .department-node {
  border-color: var(--border-color);
  box-shadow: 0 7px 18px var(--shadow-color);
  transform: translateY(-1px);
}

.department-node--root {
  border-color: rgba(7, 134, 201, 0.24);
  background: linear-gradient(135deg, var(--primary-soft), var(--surface-elevated) 72%);
}

.department-node__main {
  display: flex;
  min-width: 0;
  align-items: center;
}

.department-node__copy {
  display: flex;
  min-width: 0;
  flex-direction: column;
  gap: 5px;
}

.department-node__title-row {
  display: flex;
  min-width: 0;
  align-items: center;
  gap: 8px;
}

.department-node__title-row strong {
  overflow: hidden;
  color: var(--text-primary);
  font-size: 14px;
  font-weight: 600;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.department-node__leader {
  overflow: hidden;
  color: var(--text-muted);
  font-size: 12px;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.department-node__leader i {
  margin-right: 4px;
}

.department-node__actions {
  flex: 0 0 auto;
  opacity: 0;
  transition: opacity 0.2s ease;
}

.department-node:hover .department-node__actions,
.department-node:focus-within .department-node__actions {
  opacity: 1;
}

.department-node__menu-button {
  display: inline-flex;
  width: 34px;
  height: 34px;
  align-items: center;
  justify-content: center;
  padding: 0;
  border: 1px solid transparent;
  border-radius: 9px;
  background: transparent;
  color: var(--text-muted);
  font-size: 17px;
  cursor: pointer;
}

.department-node__menu-button:hover,
.department-node__menu-button:focus-visible {
  border-color: var(--border-color);
  background: var(--surface-hover);
  color: var(--primary-color);
  outline: none;
}

.department-action-menu.el-dropdown-menu {
  min-width: 176px;
  padding: 7px;
  border-color: var(--border-color);
  border-radius: 12px;
  background: var(--surface-elevated);
  box-shadow: 0 14px 34px var(--shadow-color);
}

.department-action-menu .el-dropdown-menu__item {
  display: flex;
  min-height: 40px;
  align-items: center;
  gap: 9px;
  padding: 0 11px;
  border-radius: 8px;
  color: var(--text-regular);
  line-height: 40px;
}

.department-action-menu .el-dropdown-menu__item i {
  margin-right: 0;
}

.department-action-menu .el-dropdown-menu__item:focus,
.department-action-menu .el-dropdown-menu__item:not(.is-disabled):hover {
  background: var(--surface-hover);
  color: var(--primary-color);
}

.department-action-menu .el-dropdown-menu__item--divided {
  border-top-color: var(--border-light);
}

.department-action-menu .department-action-menu__danger,
.department-action-menu .department-action-menu__danger:focus,
.department-action-menu .department-action-menu__danger:not(.is-disabled):hover {
  color: var(--status-danger-text);
}

.department-action-menu .department-action-menu__danger:focus,
.department-action-menu .department-action-menu__danger:not(.is-disabled):hover {
  background: rgba(232, 87, 87, 0.08);
}

.department-dialog .el-dialog {
  max-width: calc(100% - 32px);
  border-radius: 16px;
}

.department-dialog .el-dialog__body {
  padding-top: 8px;
}

.department-dialog .el-form-item:last-child {
  margin-bottom: 0;
}

.department-dialog .el-select {
  width: 100%;
}

.department-dialog__description,
.department-dialog__message {
  margin: 0 0 20px;
  color: var(--text-regular);
  font-size: 13px;
  line-height: 1.7;
}

.department-dialog__message {
  margin-bottom: 0;
}

@media (max-width: 700px) {
  .organization-page {
    gap: 14px;
  }

  .organization-intro {
    align-items: flex-start;
    flex-direction: column;
    gap: 14px;
    padding: 18px;
  }

  .organization-intro__main {
    align-items: flex-start;
  }

  .organization-intro__icon {
    width: 44px;
    height: 44px;
    border-radius: 13px;
    font-size: 20px;
  }

  .organization-intro p {
    font-size: 12px;
  }

  .organization-access-note {
    align-self: stretch;
    justify-content: center;
  }

  .organization-panel {
    padding: 15px;
  }

  .organization-toolbar {
    align-items: stretch;
    flex-direction: column;
    gap: 13px;
    padding-bottom: 15px;
  }

  .organization-search {
    width: 100%;
  }

  .organization-tree {
    padding-top: 9px;
  }

  .organization-page .el-tree-node__content {
    min-height: 64px;
  }

  .department-node {
    gap: 7px;
    padding: 8px;
  }

  .department-node__actions {
    opacity: 1;
  }

  .department-node__menu-button {
    width: 32px;
    height: 32px;
  }

}
</style>
